import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type EmptyFolderConfirmation,
  type EmptyFolderResult,
  type Folder,
  type MarkFolderReadResult,
} from '@/api';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import { STALE_TIMES } from '@/api/queryClient';
import { useAccountContext } from './accounts';

/**
 * Folder tree for ONE account — the active one unless a provider was given
 * another (see `FolderProvider`'s `accountId`).
 *
 * Shared between the sidebar (which renders the tree) and the mail view (which
 * resolves the folder id in the URL), so both see one fetch and one cache.
 */
export interface FolderContextValue {
  folders: Folder[];
  byId: Map<string, Folder>;
  inbox: Folder | undefined;
  folderByRole: (role: Folder['role']) => Folder | undefined;
  /**
   * The hierarchy delimiter this account's server reports, read off the folder
   * list (INBOX's, else the first folder that reported one) and never written
   * down: it is '.' on the Dovecot behind Poste and '/' on plenty of others.
   * Empty means a flat namespace, where nothing can be nested.
   */
  delimiter: string;
  isLoading: boolean;
  error: Error | undefined;
  /**
   * How many times the folder fetch has failed, **including while it is still
   * retrying** — the same count, for the same reason, as
   * {@link AccountFolders.failureCount}.
   *
   * The retry ladder for a 503 runs for the better part of a minute, and a
   * branch of the sidebar that showed a loading skeleton for that long is
   * indistinguishable from a slow mailbox. Consumers that have room to say so
   * speak up on the second failure instead of waiting for the query to settle.
   */
  failureCount: number;
  reload: () => void;
  /**
   * Refetch one folder: the tree's counts and every cached list of that
   * folder's mail (whatever its sort or search), plus the merged "All
   * inboxes" list when it is an Inbox. Resolves once they have answered.
   */
  refreshFolder: (folder: Folder) => Promise<void>;
  /**
   * Whether this folder may be renamed or deleted from here at all.
   *
   * The server refuses the same thing with `409 conflict` — this is the UI
   * half, so the affordance is simply absent rather than offered and then
   * taken away. It is deliberately NOT a permission check: it is "the app
   * resolves this folder by role, and loses it if it moves" (see
   * `protectedBy`).
   */
  canModify: (folder: Folder) => boolean;
  /**
   * The role folder that makes `folder` untouchable — the folder itself when
   * it holds a role, otherwise the first role folder BELOW it, because
   * renaming a parent moves its children with it. `undefined` when the folder
   * is an ordinary one.
   */
  protectedBy: (folder: Folder) => Folder | undefined;
  /** Every folder below this one, deepest first. */
  descendantsOf: (folder: Folder) => Folder[];
  createFolder: (input: { name: string; parentId?: string }) => Promise<Folder>;
  renameFolder: (folderId: string, name: string) => Promise<Folder>;
  /** `recursive` is "and everything in it", which the confirmation has shown. */
  deleteFolder: (folderId: string, recursive: boolean) => Promise<void>;
  markFolderRead: (folderId: string) => Promise<MarkFolderReadResult>;
  /** The folder id whose mark-read is in flight, if any. */
  markingReadId: string | undefined;
  /**
   * Empties a folder. Without `confirmation` it only ASKS: the server answers
   * `409 confirm_empty` with the count and the mode, or `emptiedCount: 0`
   * when there is nothing to do. See `FoldersApi.empty`.
   */
  emptyFolder: (
    folderId: string,
    confirmation?: EmptyFolderConfirmation,
  ) => Promise<EmptyFolderResult>;
}

const FolderContext = createContext<FolderContextValue | null>(null);

/**
 * @param accountId Whose folders this provider holds. The active account when
 * it is omitted, which is what the shell mounts once above every routed view.
 *
 * The sidebar mounts a second, *nested* one per connected mailbox so that one
 * `FolderTree` can draw any mailbox's folders — counts, retry and the per-folder
 * menu included — without any of them being wired to the wrong account. It
 * reads and writes the same `queryKeys.account(id).folders()` key as everything
 * else, so a mailbox the sidebar has already expanded costs the mail view
 * nothing when it becomes the active one.
 */
export function FolderProvider({
  children,
  accountId: forAccountId,
}: {
  children: ReactNode;
  accountId?: string | undefined;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { activeAccountId } = useAccountContext();
  const ownerId = forAccountId ?? activeAccountId;

  // Account-scoped key: switching accounts paints the other tree from cache
  // rather than blanking the sidebar, and never shows account A's folders
  // under account B.
  const query = useQuery({
    queryKey: queryKeys.account(ownerId ?? '').folders(),
    queryFn: ({ signal }) => api.folders.list(ownerId ?? '', signal),
    enabled: ownerId !== undefined,
    staleTime: STALE_TIMES.metadata,
  });

  const refetch = query.refetch;
  const reload = useCallback(() => void refetch(), [refetch]);

  // One invalidation for every mutation. Nothing here is optimistic: a folder
  // mutation is a round trip to an IMAP server that may refuse it, and a tree
  // that showed the new name and then snapped back is worse than one that
  // waited. Mark-read in particular must not zero a badge it may only have
  // half cleared.
  const accountId = ownerId ?? '';
  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.account(accountId).folders() }),
    [queryClient, accountId],
  );

  const create = useMutation({
    mutationFn: (input: { name: string; parentId?: string }) =>
      api.folders.create(accountId, input),
    onSuccess: invalidate,
  });
  const rename = useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      api.folders.rename(accountId, folderId, name),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: ({ folderId, recursive }: { folderId: string; recursive: boolean }) =>
      api.folders.remove(accountId, folderId, recursive),
    onSuccess: async () => {
      await invalidate();
      // The messages of a deleted folder are gone with it; leaving their
      // cached pages behind would repaint a list of mail that no longer
      // exists if anything navigated back to it.
      // Not awaited: the caller moves off a deleted open folder as soon as
      // this resolves, and awaiting would first refetch that folder's list
      // into a 404 painted as "Couldn't load this folder" under the dialog.
      void queryClient.invalidateQueries({ queryKey: queryKeys.account(accountId).messagesScope() });
    },
  });
  const markRead = useMutation({
    mutationFn: (folderId: string) => api.folders.markAllRead(accountId, folderId),
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: queryKeys.account(accountId).messagesScope() });
    },
  });

  // Emptying moves mail into Trash or removes it, so every count in the tree
  // and every cached page of the folder (and of Trash) is stale afterwards --
  // including after a run that stopped part way, which answers 200 too. The
  // unconfirmed ask changes nothing, but a refusal is an error and never
  // reaches onSuccess, and a 200 on an already-empty folder costs one refetch.
  const empty = useMutation({
    mutationFn: ({
      folderId,
      confirmation,
    }: {
      folderId: string;
      confirmation: EmptyFolderConfirmation | undefined;
    }) => api.folders.empty(accountId, folderId, confirmation),
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: queryKeys.account(accountId).messagesScope() });
    },
  });

  const refreshFolder = useCallback(
    async (folder: Folder) => {
      await Promise.all([
        invalidate(),
        queryClient.invalidateQueries({
          queryKey: queryKeys.account(accountId).messagesScope(),
          // Only this folder's lists: the key's fourth element is its filters.
          predicate: (query) => {
            const filters = query.queryKey[3];
            return (
              typeof filters === 'object' &&
              filters !== null &&
              (filters as { folderId?: unknown }).folderId === folder.id
            );
          },
        }),
        ...(folder.role === 'inbox'
          ? [queryClient.invalidateQueries({ queryKey: queryKeys.unifiedMessagesScope() })]
          : []),
      ]);
    },
    [accountId, invalidate, queryClient],
  );

  const createFolder = create.mutateAsync;
  const renameMutate = rename.mutateAsync;
  const removeMutate = remove.mutateAsync;
  const markReadMutate = markRead.mutateAsync;
  const markingReadId = markRead.isPending ? markRead.variables : undefined;
  const emptyMutate = empty.mutateAsync;
  // Stable across folder refetches, unlike the object below: the empty dialog
  // asks for its count in an effect keyed on this function, and a refetch --
  // the one its own confirmation causes, or a live update -- must not send
  // the question again.
  const emptyFolder = useCallback(
    (folderId: string, confirmation?: EmptyFolderConfirmation) =>
      emptyMutate({ folderId, confirmation }),
    [emptyMutate],
  );

  const value = useMemo<FolderContextValue>(() => {
    const folders = query.data ?? [];
    const byId = new Map(folders.map((folder) => [folder.id, folder]));
    const folderByRole = (role: Folder['role']) => folders.find((folder) => folder.role === role);
    const delimiter =
      folders.find((folder) => folder.role === 'inbox' && folder.delimiter !== '')?.delimiter ??
      folders.find((folder) => folder.delimiter !== '')?.delimiter ??
      '';

    // Descendants come from `parentId`, never from splitting `path` on a
    // delimiter: the delimiter is per folder and can appear inside a mailbox
    // name, and `parentId` is the server's own answer to the same question.
    const childrenOf = new Map<string, Folder[]>();
    for (const folder of folders) {
      if (folder.parentId === null) continue;
      const siblings = childrenOf.get(folder.parentId) ?? [];
      siblings.push(folder);
      childrenOf.set(folder.parentId, siblings);
    }
    const descendantsOf = (folder: Folder): Folder[] => {
      const out: Folder[] = [];
      const walk = (id: string): void => {
        for (const child of childrenOf.get(id) ?? []) {
          walk(child.id);
          out.push(child);
        }
      };
      walk(folder.id);
      return out;
    };
    const protectedBy = (folder: Folder): Folder | undefined =>
      folder.role !== 'custom'
        ? folder
        : descendantsOf(folder).find((child) => child.role !== 'custom');

    return {
      folders,
      byId,
      inbox: folderByRole('inbox'),
      folderByRole,
      delimiter,
      isLoading: query.isLoading,
      error: query.error ?? undefined,
      failureCount: query.failureCount,
      reload,
      refreshFolder,
      canModify: (folder) => protectedBy(folder) === undefined,
      protectedBy,
      descendantsOf,
      createFolder,
      renameFolder: (folderId, name) => renameMutate({ folderId, name }),
      deleteFolder: (folderId, recursive) => removeMutate({ folderId, recursive }),
      markFolderRead: markReadMutate,
      markingReadId,
      emptyFolder,
    };
  }, [
    query.data,
    query.isLoading,
    query.error,
    query.failureCount,
    reload,
    refreshFolder,
    createFolder,
    renameMutate,
    removeMutate,
    markReadMutate,
    markingReadId,
    emptyFolder,
  ]);

  return <FolderContext.Provider value={value}>{children}</FolderContext.Provider>;
}

export function useFolderContext(): FolderContextValue {
  const context = useContext(FolderContext);
  if (!context) throw new Error('useFolderContext must be used inside <FolderProvider>');
  return context;
}

/* ------------------------------------------------ folders of many accounts */

/** One account's folders, as much of them as the unified view needs. */
export interface AccountFolders {
  accountId: string;
  folders: Folder[];
  inbox: Folder | undefined;
  byRole: (role: Folder['role']) => Folder | undefined;
  /**
   * The folder list has not answered yet.
   *
   * Distinguishing "not yet" from "not at all" is what lets the unified view
   * name a mailbox it could not read instead of quietly leaving it out (ADR
   * 0055 §3) — a mailbox whose folder list fails never becomes a lane, so
   * without this it would vanish with nothing said.
   */
  isPending: boolean;
  /**
   * How many times the fetch has failed, **including while it is still
   * retrying**.
   *
   * The retry ladder for a 503 runs for the better part of a minute, and a
   * list that quietly omitted a mailbox for that long would be exactly the
   * silent degradation this design refuses. So the unified view speaks up on
   * the second failure rather than waiting for the query to give up.
   */
  failureCount: number;
  /** Why the folder list failed — the settled error, or the last retry's. */
  error: Error | undefined;
}

/**
 * Folder trees for several accounts at once (ADR 0056).
 *
 * The unified view needs two things per row's account that `FolderProvider`
 * only has for the active one: which folder is its Inbox (the lane's source)
 * and which is its Archive and Trash (so `e` and `#` on a row act inside that
 * row's mailbox).
 *
 * It reads through `queryKeys.account(id).folders()` — the *same* key the
 * provider uses — so the active account's tree is shared rather than fetched
 * twice, and the mail view can use this one resolver in both modes.
 */
export function useAccountFolders(accountIds: readonly string[]): Map<string, AccountFolders> {
  const api = useApi();
  const ids = useMemo(() => [...new Set(accountIds)].sort(), [accountIds]);

  /*
   * `combine` rather than a `useMemo`: the account set is dynamic, so a memo's
   * dependency list would change length, and TanStack re-runs this only when a
   * result actually changes. That identity matters — consumers put the
   * resolver in their own dependency lists, and a new object every render
   * would re-run their effects forever.
   */
  return useQueries({
    queries: ids.map((accountId) => ({
      queryKey: queryKeys.account(accountId).folders(),
      queryFn: ({ signal }: { signal: AbortSignal }) => api.folders.list(accountId, signal),
      staleTime: STALE_TIMES.metadata,
    })),
    combine: (results) => {
      const byAccount = new Map<string, AccountFolders>();
      ids.forEach((accountId, index) => {
        const folders = results[index]?.data ?? [];
        byAccount.set(accountId, {
          accountId,
          folders,
          inbox: folders.find((folder) => folder.role === 'inbox'),
          byRole: (role) => folders.find((folder) => folder.role === role),
          isPending: results[index]?.isPending ?? true,
          failureCount: results[index]?.failureCount ?? 0,
          error: results[index]?.error ?? results[index]?.failureReason ?? undefined,
        });
      });
      return byAccount;
    },
  });
}
